import React, { type ChangeEvent } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { UseFormReturn } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import { uploadTokenImage } from "@/api/tokens";
import type { Persona } from "@/api/personas";
import { EditablePersonaHeader } from "../manage-personas/EditablePersonaHeader";

import type { MintTokenFormSchema } from "./MintTokenForm";
import { FormControl, FormField, FormItem, FormMessage } from "../ui/form";

export const apiUrl = import.meta.env.PROD
  ? import.meta.env.VITE_REACT_API_URL_PRODUCTION
  : import.meta.env.VITE_REACT_API_URL_DEVELOPMENT;

interface MintTokenDetailsProps {
  form: UseFormReturn<MintTokenFormSchema>;
  tokenImageUrl: string | null;
  setTokenImageUrl: (url: string) => void;
  personaData: Persona;
}

// Renders either the State 1 or State 2/3 layouts
const MintTokenDetails: React.FC<MintTokenDetailsProps> = ({
  form,
  tokenImageUrl,
  setTokenImageUrl,
  personaData,
}) => {
  const { toast } = useToast();

  const handleFileChange = async (
    personaId: string,
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      try {
        const uploadResponse = await uploadTokenImage(personaId, selectedFile);
        const avatarId = uploadResponse.file;
        setTokenImageUrl(avatarId);
        form.setValue("tokenImage", selectedFile);

        return avatarId;
      } catch (error) {
        console.error("Error uploading avatar:", error);
        toast({ description: "Error uploading image" });
      }
    }
  };

  return (
    <>
      <div className="flex items-center gap-4">
        <div className="flex-1" data-mint-page-tour="token-image">
          <EditablePersonaHeader
            name={personaData.name ?? ""}
            imageSrc={`${apiUrl}/get-file?url=persona-knowledge-base/${personaData.persona_id}/${tokenImageUrl}`}
            buttonText="Upload Token Image"
            onAvatarChange={(e) => handleFileChange(personaData.persona_id, e)}
            gender={personaData.gender || "male"}
          />
        </div>
      </div>

      <div className="space-y-4" data-mint-page-tour="token-details">
        <div className="w-full flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="w-full sm:w-1/2">
            <label className="text-sm font-medium">Token Name</label>
            <FormField
              control={form.control}
              name="tokenName"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="w-full sm:w-1/2">
            <label className="text-sm font-medium">Token Symbol</label>
            <FormField
              control={form.control}
              name="tokenSymbol"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium">Token Description</label>
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Textarea rows={4} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
    </>
  );
};

export default MintTokenDetails;
